// ** React Imports
import { Fragment, ElementType, useMemo, useCallback, memo } from 'react'

// ** Next Import
import Link from 'next/link'
import { useRouter } from 'next/router'

// ** MUI Imports
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Box, { BoxProps } from '@mui/material/Box'
import ListItemButton, { ListItemButtonProps } from '@mui/material/ListItemButton'

// ** Third Party Imports
import clsx from 'clsx'

// ** Configs Import
import themeConfig from 'src/configs/themeConfig'

// ** Utils
import { handleURLQueries, resolveNavPath } from 'src/@core/layouts/utils'

// ** Type Import
import { NavGroup, NavLink, LayoutProps } from 'src/@core/layouts/types'

// ** Custom Components Imports
import Translations from 'src/layouts/components/Translations'
import CanViewNavGroup from 'src/layouts/components/acl/CanViewNavGroup'
import CanViewNavLink from 'src/layouts/components/acl/CanViewNavLink'

interface Props {
  item: NavGroup
  settings: LayoutProps['settings']
  navVisible?: boolean
  groupActive: string[]
  toggleNavVisibility?: () => void
}

const MenuItemTextWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.menuTextTruncate && { overflow: 'hidden' })
}))

const StyledList = styled(List)<{ isActive?: boolean }>(({ theme, isActive }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: '#ffffff',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
  borderRadius: theme.spacing(1.5),
  minWidth: 260,
  maxWidth: 340,
  border: `1px solid ${theme.palette.divider}`,
  position: 'fixed',
  left: '90px',
  zIndex: 1300,
  marginTop: theme.spacing(1),
  display: isActive ? 'flex' : 'none',
  flexDirection: 'column',
  '& .MuiListItem-root': {
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0
    }
  }
}))

const HoverNavLink = styled(ListItemButton)<
  ListItemButtonProps & { component?: ElementType; href: string; target?: '_blank' | undefined }
>(({ theme }) => ({
  width: '100%',
  borderRadius: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(0.25),
  '&:hover': {
    backgroundColor: `${theme.palette.customColors.trackBg}`,
    '& .MuiTypography-root': {
      color: `${theme.palette.primary.main}`,
      fontWeight: 500
    }
  },
  '&.active': {
    backgroundColor: `${theme.palette.customColors.trackBg}`,
    '&:hover': {
      backgroundColor: `${theme.palette.customColors.light}`
    },
    '& .MuiTypography-root': {
      color: 'text.primary',
      fontWeight: 600
    },
    borderBottom: `2px solid ${theme.palette.primary.main}`
  },
  '& .MuiTypography-root': {
    color: '#6b7280',
    fontWeight: 400,
    fontSize: '0.875rem',
    transition: 'all 0.2s ease-in-out'
  }
}))

const VerticalNavHoverItems = (props: Props) => {
  // ** Props
  const { item, navVisible, groupActive, toggleNavVisibility } = props

  // ** Hooks & Vars
  const router = useRouter()
  const { id } = router.query

  // ** Memoize router-dependent values
  const routerState = useMemo(
    () => ({
      asPath: router.asPath,
      pathname: router.pathname
    }),
    [router.asPath, router.pathname]
  )

  // ** Check if nav link is active (optimized)
  const isNavLinkActive = useCallback(
    (navItem: NavLink) => {
      if (!navItem.path) return false

      const path = resolveNavPath(navItem.path, id as string | undefined)
      const { asPath, pathname } = routerState

      // Exact path match (most common case first)
      if (asPath === path || pathname === path) return true

      // Handle URL queries
      if (handleURLQueries(router, path)) return true

      // Optimized specific route patterns
      return [
        () => asPath.includes('/app/products/') && path.includes('/app/products/'),
        () => path === '/app/restaurant/allocation' && asPath.startsWith('/app/restaurant/allocation'),
        () => path === '/app/user-management/profile' && asPath.startsWith('/app/user-management/profile'),
        () => path === '/app/user-management/list' && asPath.startsWith('/app/user-management/list'),
        () => path === '/app/user-management/changes' && asPath.startsWith('/app/user-management/changes')
      ].some(condition => condition())
    },
    [id, routerState, router]
  )

  // ** Handle nav link click
  const handleNavLinkClick = useCallback(
    (navItem: NavLink) => (e: React.MouseEvent) => {
      if (navItem.path === undefined) {
        e.preventDefault()
        e.stopPropagation()
      }
      if (navVisible && toggleNavVisibility) {
        toggleNavVisibility()
      }
    },
    [navVisible, toggleNavVisibility]
  )

  // ** Render Nav Link (memoized)
  const renderNavLink = useCallback(
    (navItem: NavLink, index: number) => {
      const path = resolveNavPath(navItem.path || '/', id as string | undefined)
      const isActive = isNavLinkActive(navItem)

      return (
        <CanViewNavLink navLink={navItem} key={index}>
          <ListItem disablePadding className='nav-link'>
            <HoverNavLink
              component={Link}
              {...(navItem.disabled && { tabIndex: -1 })}
              className={clsx({ active: isActive })}
              href={path}
              {...(navItem.openInNewTab ? { target: '_blank' } : null)}
              onClick={handleNavLinkClick(navItem)}
              sx={{
                width: '100%',
                ...(navItem.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' })
              }}
            >
              <MenuItemTextWrapper>
                <Typography variant='body1' {...(themeConfig.menuTextTruncate && { noWrap: true })}>
                  <Translations text={navItem.title} />
                </Typography>
                {navItem.badgeContent && (
                  <Chip
                    size='small'
                    label={navItem.badgeContent}
                    color={navItem.badgeColor || 'primary'}
                    sx={{
                      height: 20,
                      minWidth: 20,
                      '& .MuiChip-label': { px: 1, fontSize: '0.75rem', textTransform: 'capitalize' }
                    }}
                  />
                )}
              </MenuItemTextWrapper>
            </HoverNavLink>
          </ListItem>
        </CanViewNavLink>
      )
    },
    [handleNavLinkClick, id, isNavLinkActive]
  )

  // ** Render Nav Group (memoized)
  const renderNavGroup = useCallback(
    (navItem: NavGroup, index: number) => {
      const hasChildren = 'children' in navItem && navItem.children && navItem.children.length > 0

      return (
        <CanViewNavGroup navGroup={navItem} key={index}>
          <Fragment>
            {/* Group Title */}
            <ListItem disablePadding className='nav-group-title'>
              <ListItemButton
                disabled
                sx={{
                  py: 1.5,
                  px: 3,
                  cursor: 'default',
                  '&.Mui-disabled': {
                    opacity: 1,
                    color: 'text.primary'
                  }
                }}
              >
                <MenuItemTextWrapper>
                  <Typography
                    variant='body1'
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    <Translations text={navItem.title} />
                  </Typography>
                  {navItem.badgeContent && (
                    <Chip
                      size='small'
                      label={navItem.badgeContent}
                      color={navItem.badgeColor || 'primary'}
                      sx={{
                        height: 18,
                        minWidth: 18,
                        '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', textTransform: 'capitalize' }
                      }}
                    />
                  )}
                </MenuItemTextWrapper>
              </ListItemButton>
            </ListItem>

            {/* Group Children */}
            {hasChildren && 'children' in navItem && (
              <Box sx={{ pl: 1 }}>
                {navItem.children?.map((child, childIndex) => {
                  if ('children' in child && child.children && child.children.length > 0) {
                    return renderNavGroup(child, childIndex)
                  } else {
                    return renderNavLink(child as NavLink, childIndex)
                  }
                })}
              </Box>
            )}
          </Fragment>
        </CanViewNavGroup>
      )
    },
    [renderNavLink]
  )

  // ** Memoize active state calculation
  const isCurrentGroupActive = useMemo(() => {
    return groupActive?.includes(item.title) || false
  }, [groupActive, item.title])

  // ** Early return if no children
  if (!('children' in item) || !item.children || item.children.length === 0) {
    return null
  }

  return (
    <StyledList isActive={isCurrentGroupActive}>
      {/* Group Header */}
      <ListItem disablePadding>
        <ListItemButton
          disabled
          sx={{
            px: 2,
            py: 1.5,
            cursor: 'default',
            '&.Mui-disabled': {
              opacity: 1
            }
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.5
            }}
          >
            <Translations text={item.title} />
          </Typography>
          {item.badgeContent && (
            <Chip
              size='small'
              label={item.badgeContent}
              color={item.badgeColor || 'primary'}
              sx={{
                ml: 2,
                height: 22,
                minWidth: 22,
                '& .MuiChip-label': { px: 1, fontSize: '0.75rem', textTransform: 'capitalize' }
              }}
            />
          )}
        </ListItemButton>
      </ListItem>

      {/* Render Children */}
      {item.children.map((child, index) => {
        if ('children' in child && child.children && child.children.length > 0) {
          return renderNavGroup(child, index)
        } else {
          return renderNavLink(child as NavLink, index)
        }
      })}
    </StyledList>
  )
}

export default memo(VerticalNavHoverItems)
